import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { ApplicationStore, ApplicationStoreType } from '../core/store/application.store';
import { ButtonComponent, ButtonType } from '../shared/components/button/button.component';
import { environment } from '../../environments/environment';

@Component({
    selector: 'juyo-no-access-page',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: 'no-access.scss',
    imports: [NgOptimizedImage, ButtonComponent],
    template: `
        <div class="page-body">
            <div class="form-container">
                <div class="panel">
                    <img
                        ngSrc="assets/logos/logo_white.png"
                        width="415"
                        height="129"
                        class="logo"
                        alt="Juyo logo"
                        priority
                    />
                    <div class="heading-section">
                        <h1 class="heading">No Access</h1>
                    </div>
                    @if (canNavigateToAnalytics()) {
                        <juyo-button
                            [title]="'BACK'"
                            [useFullWidth]="true"
                            [buttonType]="ButtonType.PURPLE"
                            (clicked)="onBack()"
                        >
                            Back to Analytics
                        </juyo-button>
                    } @else {
                        <div class="access-section">
                            <p class="message">
                                Oops! It looks like you don't have access to this space yet. Sign in again to refresh
                                your access.
                            </p>
                            <juyo-button
                                [title]="'SIGN IN'"
                                [useFullWidth]="true"
                                [buttonType]="ButtonType.PURPLE"
                                (clicked)="onSignInAgain()"
                            >
                                Sign in again
                            </juyo-button>
                            <p class="help-message">
                                If you're still stuck, clear your browser cache for all time and try again, or contact
                                us at
                                <a href="mailto:support@juyoanalytics.com">support@juyoanalytics.com</a>.
                            </p>
                        </div>
                    }
                </div>
            </div>
            <footer class="footer">
                <span>© 2026 Juyo Analytics</span>
                <span class="separator">·</span>
                <a target="_blank" href="https://juyoanalytics.com/privacy-policy-and-cookies/">Privacy</a>
                <span class="separator">·</span>
                <a target="_blank" href="https://juyoanalytics.com/privacy-policy-and-cookies/">Terms</a>
            </footer>
        </div>
    `,
})
export class NoAccessPage {
    protected readonly ButtonType = ButtonType;

    private authService = inject(AuthenticationService);
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);

    protected readonly canNavigateToAnalytics = computed<boolean>(
        () => !!this.applicationStore.selectedOrganization()?.fromV3,
    );

    protected onBack(): void {
        const tokens = this.authService.getTokensForExport();
        const encoded = btoa(JSON.stringify(tokens));
        const selectedOrganization = this.applicationStore.selectedOrganization();
        const chainParam = selectedOrganization ? `?chainId=${selectedOrganization.chainId}` : '';
        window.location.href = `${environment.externalPlatformUrl}${chainParam}#tokens=${encoded}`;
    }

    protected onSignInAgain(): void {
        void this.authService.signOut();
    }
}
